import { normalizeName } from "../parser/item-input.js";
import { ohneAkzente } from "./text.js";
import type { CategoryKey } from "./generated.js";

/**
 * Was die App über deutsche Einkaufszettel weiß, ohne zu fragen.
 *
 * Über das Mikrofon ordnet das Modell jeden Artikel einer Warengruppe zu. Wer
 * tippt oder ins Feld diktiert, bekam bisher nichts: Ein neuer Artikel landete
 * unter "Ohne Kategorie" und damit im Laden am falschen Ende. Diese Tabelle
 * schließt die Lücke ohne Netz, ohne Kosten und ohne Wartezeit.
 *
 * Erweitert am 20.09.2026 an den echten Zutaten der Familie: Von 343 Namen
 * aus den Rezepten der Spaisekammer erkannte die erste Fassung 63 Prozent,
 * vor allem Gewürze, Saucen, Kräuter und Käsesorten fehlten.
 *
 * Alles Weitere lernt der Familienkatalog von selbst, sobald jemand einem
 * Artikel einmal eine Warengruppe gibt.
 */
const WISSEN: [CategoryKey, readonly string[]][] = [
  [
    "obst_gemuese",
    [
      "apfel", "äpfel", "banane", "tomate", "gurke", "salat", "zwiebel", "schalotte", "frühlingszwiebel",
      "kartoffel", "karotte", "möhre", "paprika", "zitrone", "limette", "orange", "birne", "erdbeere",
      "himbeere", "heidelbeere", "beere", "traube", "avocado", "brokkoli", "blumenkohl", "spinat",
      "champignon", "pilz", "lauch", "porree", "sellerie", "ingwer", "knoblauch", "kürbis", "zucchini",
      "aubergine", "melone", "kiwi", "mango", "pfirsich", "kirsche", "pflaume", "zwetschge", "nektarine", "rucola",
      "radieschen", "kohlrabi", "wirsing", "rosenkohl", "spargel", "fenchel", "pak choi", "sojasprossen",
      "mais", "erbsen", "bohne", "rote bete", "pastinake", "süßkartoffel", "chicoree", "endivie", "feldsalat",
      "chili", "chilischote", "peperoni", "jalapeno",
      "petersilie", "schnittlauch", "basilikum", "koriander", "dill", "estragon", "minze", "minzblätter",
      "rosmarin", "thymian", "salbei", "oregano", "majoran", "kerbel", "liebstöckel", "kräuter",
      "obst", "gemüse", "zeste", "abrieb", "spalten",
    ],
  ],
  [
    "backwaren",
    [
      "vollkornbrot", "toastbrot", "brötchen", "semmel", "baguette", "ciabatta", "croissant", "brezel",
      "fladenbrot", "pita", "naan", "brot", "toast", "kuchen", "zwieback", "knäckebrot",
    ],
  ],
  [
    "molkerei",
    [
      "hafermilch", "buttermilch", "vollmilch", "kondensmilch", "milch", "butter", "joghurt",
      "quark", "sahne", "schlagsahne", "saure sahne", "schmand", "creme fraiche", "creme double", "skyr",
      "kefir", "ayran", "frischkäse", "hüttenkäse", "margarine", "pudding", "eier", "eigelb",
      "eiweiß", "eiklar", "tzatziki", "kimchi", "hefe", "frischhefe", "blätterteig", "pizzateig",
      "tofu", "seidentofu",
      // Wer "aus dem Kühlregal" schreibt, meint genau das, auch wenn im Namen
      // ein Wort steht, das sonst woanders hingehört.
      "kühlregal", "kühltheke",
    ],
  ],
  [
    "wurst_kaese",
    [
      "salami", "schinken", "aufschnitt", "leberwurst", "gouda", "emmentaler", "mortadella", "bacon",
      "speck", "speckwürfel", "pancetta", "chorizo", "prosciutto", "parmesan", "parmigiano", "pecorino",
      "grana padano", "mozzarella", "burrata", "feta", "halloumi", "ricotta", "mascarpone", "camembert",
      "brie", "gorgonzola", "raclette", "bergkäse", "cheddar", "wurst", "käse",
    ],
  ],
  [
    "fleisch_fisch",
    [
      "hackfleisch", "hack", "rindfleisch", "schweinefleisch", "hähnchen", "hühnchen", "pute", "steak",
      "ribeye", "entrecote", "filet", "schnitzel", "kalbsschnitzel", "kotelett", "kassler", "tafelspitz",
      "gulasch", "lammkeule", "lamm", "ente", "gans", "rinderknochen", "rinderbrühe",
      "lachs", "thunfisch", "garnelen", "gambas", "scampi", "shrimps", "sardelle", "kabeljau", "dorsch",
      "forelle", "seelachs", "muscheln", "tintenfisch", "calamari",
      "bratwurst", "würstchen", "salsiccia", "merguez", "fisch", "fleisch",
    ],
  ],
  [
    "tiefkuehl",
    [
      "tiefkühl", "tk-", "tk pizza", "tk ", "gefroren", "pizza", "fischstäbchen", "pommes", "speiseeis",
      "eiscreme", "blattspinat", "dumplings", "gyoza", "edamame",
    ],
  ],
  [
    "getraenke",
    [
      "mineralwasser", "sprudel", "wasser", "apfelsaft", "orangensaft", "saft", "cola", "limonade", "limo",
      "bier", "weißwein", "rotwein", "wein", "sekt", "prosecco", "schorle", "energy", "eistee",
      "brandy", "cognac", "rum", "whisky", "wodka", "gin", "likör", "sake", "portwein", "sherry", "marsala",
    ],
  ],
  [
    "trockensortiment",
    [
      // Nudeln, Reis, Mehl
      "spaghetti", "linguine", "tagliatelle", "penne", "fusilli", "orecchiette", "farfalle", "lasagne",
      "tortellini", "ravioli", "nudeln", "pasta", "reis", "risotto", "basmati", "couscous", "bulgur",
      "quinoa", "polenta", "grieß", "hartweizengrieß", "mehl", "panko", "paniermehl", "semmelbrösel",
      // Backen
      "zucker", "puderzucker", "vanillezucker", "backpulver", "natron", "trockenhefe", "speisestärke",
      "maisstärke", "soßenbinder", "gelatine", "kakao", "vanille",
      // Gewürze
      "salz", "pfeffer", "paprikapulver", "chilipulver", "chiliflocken", "currypulver", "curry", "kurkuma",
      "kreuzkümmel", "kümmel", "schwarzkümmel", "koriandersamen", "fenchelsamen", "senfkörner", "muskat",
      "knoblauchpulver", "zwiebelpulver", "ingwerpulver", "kartoffelstärke", "gemüsebrühe", "hühnerbrühe",
      "chiliöl", "chili öl",
      "zimt", "nelken", "piment", "kardamom", "wacholder", "lorbeer", "safran", "sumach", "garam masala",
      "ras el hanout", "gewürz", "rub", "würzer", "msg", "vegeta", "brühe", "brühpulver", "brühwürfel",
      "fond", "maggi",
      // Saucen und Öle
      "olivenöl", "sesamöl", "rapsöl", "sonnenblumenöl", "öl", "essig", "balsamico", "sojasauce",
      "austernsauce", "fischsauce", "worcestershire", "teriyaki", "hoisin", "sriracha", "sambal oelek",
      "gochujang", "gochugaru", "currypaste", "chili-crisp", "crispy chili", "sweet chili", "mirin",
      "sake-würze", "senf", "ketchup", "mayonnaise", "aioli", "tahini", "harissa", "pesto", "passata",
      "tomatenmark", "sauce", "soße",
      // Vorrat
      "haferflocken", "müsli", "cornflakes", "kaffee", "espresso", "tee", "honig", "ahornsirup",
      "marmelade", "konfitüre", "nutella", "erdnussbutter", "erdnusscreme", "linsen", "kichererbsen",
      "bohnen", "kokosmilch", "konserve", "kapern", "oliven", "sauerkraut", "sardellenfilet",
      "trockenfrüchte", "rosinen", "datteln", "mandeln", "walnüsse", "haselnüsse", "cashew", "pinienkerne",
      "sesam", "sesamsamen", "sonnenblumenkerne", "kürbiskerne",
    ],
  ],
  [
    "suesswaren",
    [
      "schokolade", "gummibärchen", "bonbon", "kekse", "chips", "riegel", "kaugummi", "praline", "nüsse",
      "salzstangen", "cracker", "popcorn", "waffeln",
    ],
  ],
  [
    "haushalt",
    [
      "toilettenpapier", "klopapier", "küchenrolle", "spülmittel", "waschmittel", "weichspüler", "müllbeutel",
      "putzmittel", "schwamm", "alufolie", "frischhaltefolie", "backpapier", "geschirrspültabs", "kerze",
      "gefrierbeutel", "spülschwamm", "allzweckreiniger",
    ],
  ],
  [
    "drogerie",
    [
      "zahnpasta", "zahnbürste", "shampoo", "duschgel", "deo", "seife", "rasierer", "taschentücher",
      "handcreme", "sonnencreme", "wattestäbchen", "zahnseide",
    ],
  ],
  ["baby", ["windeln", "feuchttücher", "babybrei", "schnuller"]],
  ["tier", ["katzenstreu", "katzenfutter", "hundefutter", "tierfutter", "leckerli"]],
];

/**
 * Wörter, die nur als ganzes Wort etwas bedeuten.
 *
 * "ei" steckt in "Reis", "Fleisch" und "Eintopf", "eis" in "Fleisch" und
 * "Preiselbeere". Als Teilwort wären sie kein Wissen, sondern ein Zufall.
 */
const GANZE_WOERTER: readonly (readonly [string, CategoryKey])[] = [
  ["ei", "molkerei"],
  ["eis", "tiefkuehl"],
  ["fett", "trockensortiment"],
];

/**
 * Alle Begriffe in einer Liste, der längste zuerst.
 *
 * Vorher entschied die Reihenfolge der Warengruppen: Wer "Pizzateig aus dem
 * Kühlregal" schrieb, landete im Tiefkühlfach, weil "pizza" in einer früheren
 * Gruppe stand als "kühlregal". Jetzt gewinnt der längste Treffer, egal in
 * welcher Gruppe er steht. Das ist auch der Grund, warum die Tabelle oben
 * nicht mehr von Hand sortiert sein muss.
 */
const BEGRIFFE: readonly (readonly [string, CategoryKey])[] = WISSEN.flatMap(([kategorie, begriffe]) =>
  begriffe.map((begriff) => [begriff, kategorie] as const),
).sort((a, b) => b[0].length - a[0].length);

export function guessCategory(name: string): CategoryKey | null {
  const gesucht = ohneAkzente(normalizeName(name));
  if (gesucht.length === 0) return null;

  // Teilwort genügt: Im Deutschen steckt die Ware im zusammengesetzten Wort,
  // "Vollkornbrot" ist ein Brot und "Hafermilch" eine Milch. Der längste
  // Treffer gewinnt, deshalb schlägt "buttermilch" sowohl "butter" als auch
  // "milch" und "chilipulver" das bloße "chili".
  // Erst die ganzen Wörter: Sie sind zu kurz, um irgendwo mitten im Wort etwas
  // zu bedeuten, und würden als Teilwort mehr kaputt machen als sie treffen.
  const woerter = gesucht.split(/[\s-]+/u);
  for (const [wort, kategorie] of GANZE_WOERTER) {
    if (woerter.includes(wort)) return kategorie;
  }

  for (const [begriff, kategorie] of BEGRIFFE) {
    if (gesucht.includes(ohneAkzente(begriff))) return kategorie;
  }

  return null;
}
